<template>
  <div>
    <validation-observer
      ref="formValidation"
      v-slot="{ handleSubmit, invalid }"
    >
      <b-form
        novalidate
        @submit.stop.prevent="handleSubmit(showConfirmSubmit)"
      >
        <Panel size="small">
          <template #header>
            <span data-cy="heading">Create Campaign</span>
          </template>

          <template #content>
            <div>
              <b-row>
                <b-col md="3" />
                <b-col>
                  <b-alert
                    show
                    variant="info"
                    class="mb-3"
                    style="margin-left: -5px;"
                  >
                    <font-awesome-icon
                      :icon="['fas', 'info-circle']"
                      class="mr-2"
                    />
                    Calls are answered by you, so there is no destination number to configure. US
                    calls only. Only one campaign per category and language. Only one campaign can
                    be active at a time.
                  </b-alert>
                </b-col>
              </b-row>

              <b-alert
                :show="hasNoCategoryLeft"
                variant="warning"
                class="mb-3"
                data-cy="alert-no-category-left"
              >
                <font-awesome-icon
                  :icon="['fas', 'exclamation-triangle']"
                  class="mr-2"
                />
                You already have a campaign for every category and language available to you. Use
                those campaigns instead of creating another one.
              </b-alert>

              <BaseFormSelect
                id="buyer-create-campaign__campaign-category"
                v-model="categoryId"
                :horizontal="true"
                internal-platform-input
                :small="true"
                name="Category"
                data-cy="select-category"
                label="Category"
                text-field="displayName"
                value-field="id"
                not-enabled-field="notEnabled"
                :options="categoryList"
                required
                width="5"
                width-lg="4"
                width-xl="3"
                has-tooltip
              />

              <!-- A category with a single language leaves the buyer nothing to choose, so it is
                   preselected and the dropdown stays hidden. -->
              <BaseFormSelect
                v-if="showLanguageSelect"
                id="buyer-create-campaign__campaign-language"
                v-model="languageId"
                :horizontal="true"
                internal-platform-input
                :small="true"
                name="Language"
                data-cy="select-language"
                label="Language"
                text-field="name"
                value-field="id"
                not-enabled-field="notEnabled"
                :options="languageOptions"
                required
                width="5"
                width-lg="4"
                width-xl="3"
                has-tooltip
              />
            </div>
          </template>
        </Panel>

        <Audience softphone />

        <Budget
          :invalid="invalid"
          :minimum-initial-deposit-satisfied="isMinimumInitialDepositRequirementSatisfied"
          :loading="isLoading"
          :selected-category="selectedCategory"
          softphone
          @submit="showConfirmSubmit"
        />
      </b-form>
    </validation-observer>

    <b-modal
      id="confirm-create-softphone-campaign-modal"
      ref="confirm-create-softphone-campaign-modal"
      header-class="custom-modal"
      body-class="custom-modal"
      footer-class="custom-modal"
      title="Confirm Campaign Creation"
      button-size="sm"
      no-close-on-backdrop
      @hidden="termsAccepted = false"
    >
      <p>
        Agents must service all qualified calls in good faith. Valid calls disconnected or rejected to avoid payment or due to agent mishandling may be deemed acceptable and billable. Repeated violations may result in force-billing, suspension, or termination of service.
      </p>

      <b-form-checkbox
        v-model="termsAccepted"
        data-cy="checkbox-accept-terms"
        class="mb-0"
      >
        I acknowledge and accept the above terms regarding the handling of qualified calls and the potential consequences of non-compliance.
      </b-form-checkbox>

      <template #modal-footer>
        <b-button
          type="button"
          variant="secondary"
          size="sm"
          :disabled="isLoading"
          @click="$bvModal.hide('confirm-create-softphone-campaign-modal')"
        >
          Cancel
        </b-button>

        <BaseButtonLoader
          :loading="isLoading"
          class="ml-2"
          type="button"
          :custom-button="false"
          size="sm"
          variant="primary"
          :disabled="!termsAccepted"
          data-cy="button-confirm-create-campaign"
          @click="submitForm"
        >
          Confirm
        </BaseButtonLoader>
      </template>
    </b-modal>
  </div>
</template>

<script>
import { mapActions, mapGetters, mapMutations, mapState } from 'vuex'

import Audience from './Audience.vue'
import Budget from './Budget.vue'
import { showErrorMessage, showSuccessMessage } from '@/notification-utils'
import { ACCOUNT_REQUIREMENTS, dashboardNotificationForAccountRequirementsMessages } from '@/store/modules/auth.store'
import {
  buildSoftphoneCampaignName,
  campaignRoutingTypes,
  destinationTypes,
  getBrowserTimeZone,
  getTakenSoftphoneCampaignCombos,
  getTakenSoftphoneLanguageIds,
  isSoftphoneCampaignComboTaken,
  isSoftphoneCategoryTaken,
  isSoftphoneEligibleCategory,
  resolveSoftphoneCampaignLanguageId,
  resolveSoftphoneCampaignTimeZoneId,
  SOFTPHONE_CAMPAIGN_COUNTRY_ID,
  SOFTPHONE_COMBO_TAKEN_SUFFIX,
  SOFTPHONE_DUPLICATE_CAMPAIGN_MESSAGE
} from '@/store/modules/campaign.store'
import { getSelectableCategories } from '@/utils/campaignCategories'

export default {
  name: 'BuyerCreateSoftphoneCampaign',

  title: 'Create campaign',

  components: {
    Audience,
    Budget,
  },

  data() {
    return {
      isLoading: false,
      termsAccepted: false
    }
  },

  computed: {
    ...mapState('common-entities', ['categories']),

    ...mapGetters('account', ['allowedTrafficSources', 'allowedCategories']),

    ...mapState('campaign', ['buyerCampaign', 'buyerCampaigns']),

    ...mapGetters('auth', ['accountTimeZoneId', 'isMinimumInitialDepositRequirementSatisfied', 'userFullName', 'userId']),

    // The account's own time zone when it has one - it is optional, set on Account > Timezone -
    // else the agent's browser zone, else Eastern. Never asked for: this page has no Schedule
    // section, and a null time zone would go through unnoticed (nothing validates it) and leave
    // the campaign's budget windows measured in an unspecified zone.
    softphoneTimeZoneId() {
      return resolveSoftphoneCampaignTimeZoneId(this.accountTimeZoneId, getBrowserTimeZone())
    },

    // The API allows this agent one softphone campaign per category and language, so the
    // combinations they already hold are taken out of the pickers rather than left to fail on
    // submit. Their own campaigns only - a colleague's campaign does not block them.
    takenSoftphoneCombos() {
      return getTakenSoftphoneCampaignCombos(this.buyerCampaigns, this.userId)
    },

    // The categories the full Create Campaign page offers (see getSelectableCategories), minus the
    // ones this page cannot represent - see isSoftphoneEligibleCategory. Both stay available on the
    // full Create Campaign page.
    categoryList() {
      const categoryList = getSelectableCategories(this.allowedCategories, this.categories)
        .filter(isSoftphoneEligibleCategory)
        .map(category => {
          const isTaken = isSoftphoneCategoryTaken(this.takenSoftphoneCombos, category)

          return {
            ...category,
            notEnabled: isTaken,
            displayName: isTaken ? `${category.name} ${SOFTPHONE_COMBO_TAKEN_SUFFIX}` : category.name
          }
        })
        .sort((a, b) => a.displayName.localeCompare(b.displayName))

      categoryList.unshift({ name: 'Please select', displayName: 'Please select', value: null })

      return categoryList
    },

    // Every category the agent could pick already has one of their campaigns in every language,
    // so there is nothing left to create here - better said outright than left to be discovered
    // in a dropdown where everything is disabled.
    hasNoCategoryLeft() {
      const selectableCategories = this.categoryList.filter(category => Boolean(category.id))

      return selectableCategories.length > 0
        && selectableCategories.every(category => category.notEnabled)
    },

    // Guarded on a truthy id: the "Please select" placeholder has no id, so an unguarded lookup
    // would match it and report a selected category with no languages.
    selectedCategory() {
      return this.buyerCampaign.categoryId
        ? this.categoryList.find(category => category.id === this.buyerCampaign.categoryId) || null
        : null
    },

    // The API rejects a language the category is not configured for, so the choice is always
    // scoped to the selected category's languages.
    categoryLanguages() {
      return this.selectedCategory?.languages ?? []
    },

    // Exactly one language is not a choice - it is preselected and the dropdown is hidden. Zero
    // configured languages is not expected, but showing the (empty) required dropdown fails the
    // form instead of submitting a language the category would reject.
    showLanguageSelect() {
      return Boolean(this.selectedCategory) && this.categoryLanguages.length !== 1
    },

    // Languages of the selected category the agent has already created a campaign for. A
    // category is only offered at all while at least one of its languages is still free, so
    // this never covers the whole list.
    takenLanguageIds() {
      return getTakenSoftphoneLanguageIds(
        this.takenSoftphoneCombos,
        this.buyerCampaign.categoryId,
        this.categoryLanguages
      )
    },

    languageOptions() {
      const languageOptions = this.categoryLanguages.map(language => {
        const isTaken = this.takenLanguageIds.includes(language.id)

        return {
          ...language,
          notEnabled: isTaken,
          name: isTaken ? `${language.name} ${SOFTPHONE_COMBO_TAKEN_SUFFIX}` : language.name
        }
      })

      languageOptions.unshift({ name: 'Please select', value: null })

      return languageOptions
    },

    // The last line of defence in the UI: the pickers already exclude a taken combination, but
    // the campaign that took it may have been created after this page loaded its list.
    isSelectedComboTaken() {
      return isSoftphoneCampaignComboTaken(
        this.takenSoftphoneCombos,
        this.buyerCampaign.categoryId,
        this.buyerCampaign.languageId
      )
    },

    categoryId: {
      get() {
        return this.buyerCampaign.categoryId
      },

      set(value) {
        const category = this.categoryList.find(c => c.id === value)

        this.UPDATE_BUYER_CAMPAIGN_BASIC_DETAILS({
          name: buildSoftphoneCampaignName(category?.name, this.userFullName),
          categoryId: value,
          countryId: SOFTPHONE_CAMPAIGN_COUNTRY_ID,
          // Resolved against the languages still free for the NEW category: preselecting one
          // the agent has already used would hand them a form the API refuses.
          languageId: resolveSoftphoneCampaignLanguageId(
            category?.languages,
            this.buyerCampaign.languageId,
            getTakenSoftphoneLanguageIds(this.takenSoftphoneCombos, value, category?.languages)
          )
        })
      }
    },

    languageId: {
      get() {
        return this.buyerCampaign.languageId
      },

      set(value) {
        this.UPDATE_BUYER_CAMPAIGN_BASIC_DETAILS({
          name: this.buyerCampaign.name,
          categoryId: this.buyerCampaign.categoryId,
          countryId: SOFTPHONE_CAMPAIGN_COUNTRY_ID,
          languageId: value
        })
      }
    }
  },

  watch: {
    isMinimumInitialDepositRequirementSatisfied: {
      immediate: true,
      handler(newValue) {
        if (!newValue) {
          this.showDashboardNotification(dashboardNotificationForAccountRequirementsMessages[ACCOUNT_REQUIREMENTS.MinimumInitialDeposit])
        } else {
          this.hideDashboardNotification()
        }
      }
    },

    // Both of these come from the account details. They are normally loaded before this page
    // mounts, but the buyer cannot see or fix either field here, so re-apply them if they
    // arrive late rather than submitting an empty required value.
    allowedTrafficSources() {
      this.UPDATE_BUYER_CAMPAIGN_TRAFFIC_SOURCES({
        ...this.buyerCampaign.trafficSources,
        allowedSources: this.defaultAllowedSourceIds()
      })
    },

    // Re-applied rather than skipped when a time zone is already set: what is already there may be
    // the browser fallback, and the account's own zone wins over it whenever it arrives.
    accountTimeZoneId() {
      this.applyTimeZone()
    }
  },

  async created() {
    this.setDefaultBuyerCampaign()
    await Promise.all([this.getCategories(), this.loadExistingCampaigns()])
    this.applySoftphoneDefaults()
  },

  methods: {
    ...mapActions([
      'showDashboardNotification',
      'hideDashboardNotification'
    ]),

    ...mapActions('common-entities', ['getCategories']),

    ...mapActions('campaign', ['setDefaultBuyerCampaign', 'createBuyerCampaign', 'fetchBuyerCampaigns']),

    ...mapMutations('campaign', [
      'UPDATE_BUYER_CAMPAIGN_BASIC_DETAILS',
      'UPDATE_BUYER_CAMPAIGN_ROUTING',
      'UPDATE_BUYER_CAMPAIGN_TRAFFIC_SOURCES'
    ]),

    // Everything the buyer is not asked about. Destination, Schedule & Capacity and Traffic
    // Sources are not rendered on this page, so their values have to be set here or the API
    // rejects the request (schedule.timeZoneId and allowedSources are both required).
    applySoftphoneDefaults() {
      // No category picked yet, so no language is known either - it follows from the category.
      this.UPDATE_BUYER_CAMPAIGN_BASIC_DETAILS({
        name: buildSoftphoneCampaignName(null, this.userFullName),
        categoryId: null,
        countryId: SOFTPHONE_CAMPAIGN_COUNTRY_ID,
        languageId: null
      })

      this.UPDATE_BUYER_CAMPAIGN_ROUTING({
        ...this.buyerCampaign.routing,
        type: campaignRoutingTypes.Static,
        destinationType: destinationTypes.Softphone,
        number: '',
        sipNumber: {},
        schedule: {
          ...this.buyerCampaign.routing.schedule,
          timeZoneId: this.softphoneTimeZoneId
        }
      })

      this.UPDATE_BUYER_CAMPAIGN_TRAFFIC_SOURCES({
        ...this.buyerCampaign.trafficSources,
        allowTollFreeCallers: true,
        // Per campaign rather than inherited: the buyer-level policy defaults to not restricting.
        restrictDuplicates: 'RestrictByCampaign',
        allowThirdParties: true,
        allowedSources: this.defaultAllowedSourceIds()
      })
    },

    // Prefill with every traffic source the account can use for this call type - the same list
    // the hidden Traffic Sources section would have offered.
    defaultAllowedSourceIds() {
      const callType = this.buyerCampaign.trafficSources.callType

      return (this.allowedTrafficSources ?? [])
        .filter(source => !callType || source.trafficSourceType === callType)
        .map(source => source.id)
    },

    // The campaign's time zone is what its daily and hourly budget windows are measured in, and
    // this page has no Schedule section to ask on. Nothing is shown about it - the account's own
    // zone is the answer whenever there is one.
    applyTimeZone() {
      this.UPDATE_BUYER_CAMPAIGN_ROUTING({
        ...this.buyerCampaign.routing,
        schedule: {
          ...this.buyerCampaign.routing.schedule,
          timeZoneId: this.softphoneTimeZoneId
        }
      })
    },

    // Which combinations the agent already holds is only known from their existing campaigns.
    // A failure here costs the early warning, not the rule - the API still refuses a duplicate -
    // so it must not stop the page from loading.
    async loadExistingCampaigns() {
      try {
        await this.fetchBuyerCampaigns()
      } catch (error) {
        console.error('Failed to load the existing campaigns to check for a duplicate softphone campaign', error)
      }
    },

    showConfirmSubmit() {
      if (this.isSelectedComboTaken) {
        showErrorMessage(SOFTPHONE_DUPLICATE_CAMPAIGN_MESSAGE)

        return
      }

      this.termsAccepted = false
      this.$bvModal.show('confirm-create-softphone-campaign-modal')
    },

    async submitForm() {
      try {
        this.isLoading = true

        await this.createBuyerCampaign(this.buyerCampaign)

        this.$bvModal.hide('confirm-create-softphone-campaign-modal')

        // Not "created and ready": an approved campaign still has to be taken live from the
        // campaigns list, and until an admin approves it Go Live is refused.
        showSuccessMessage('Campaign submitted for review. You can go live once it is approved.')
        this.$router.push({ name: 'ListBuyerCampaigns' })
      } catch (error) {
        showErrorMessage(error.response?.data?.title)
      } finally {
        this.isLoading = false
      }
    }
  }
}
</script>

<style lang="scss" scoped></style>
